import {
  useGetActiveSubscriptionPlans,
  useGetPlanDetail,
  useSendPlanCancellationRequest,
} from "@/hooks/use-subscription";
import {
  IActiveSubscriptionPlan,
  IPlanCancellationRequestPayload,
  IPlanDetailResponse,
  IPurchasePlan,
  PaymentInterval,
  ReturnLikelihood,
  SubscriptionStatus,
} from "@/types/subscription.types";
import { useSearch } from "@tanstack/react-router";

import { CancelSeedListPlanSearch } from "@/routes/cancelSeedListPlan";
import { createContext, useContext, useMemo, useState } from "react";
import { toast } from "sonner";
import * as z from "zod";
import {
  SeedListFeedbackFormValues,
  formatSeedListCancellationReason,
} from "./seed-list-cancel-feedback";

const valuableFeaturesSchema = z.object({
  emailWarmup: z.boolean(),
  inboxPlacementTests: z.boolean(),
  googlePostmasterIntegration: z.boolean(),
  emailHealthTests: z.boolean(),
  customTemplates: z.boolean(),
  emailTemplateCoach: z.boolean(),
});

const cancellationPayloadSchema = z
  .object({
    feedback: z.object({
      cancellationReason: z.object({
        type: z.string().min(1, "Cancellation reason is required"),
        details: z.record(z.string()),
      }),
      improvementSuggestions: z.string(),
      serviceRating: z.number().min(0).max(5),
      valuableFeatures: valuableFeaturesSchema,
      returnLikelihood: z.nativeEnum(ReturnLikelihood),
    }),
    subscriptionId: z.string().min(1, "Subscription ID is required"),
    planId: z.string().optional(),
    addonPlanId: z.string().optional(),
    quantity: z.number().min(1, "Quantity must be at least 1"),
    assignedMailboxIds: z.array(z.string()).optional(),
  })
  .refine(
    (data) => {
      if (!data.planId && !data.addonPlanId) {
        return false;
      }
      return true;
    },
    {
      message: "Plan ID and Addon Plan ID are required",
      path: ["planId", "addonPlanId"],
    }
  );

const SeedListCancelPlanContext = createContext({
  state: {
    step: 1,
    selectedAssignedMailboxes: [] as string[],
    feedback: undefined as SeedListFeedbackFormValues | undefined,
    showConfirmationDialog: false as boolean,
    isSuccess: false as boolean,
  },
  handleStateChange: (newPartialState: Partial<SeedListCancelPlanState>) => {},
  subscription: {} as IActiveSubscriptionPlan | undefined,
  planDetails: {} as IPlanDetailResponse | undefined,
  searchParams: {} as CancelSeedListPlanSearch,
  handleConfirm: () => {},
  isCancellationPending: false as boolean,
});

type SeedListCancelPlanState = {
  step: number;
  selectedAssignedMailboxes: string[];
  feedback: SeedListFeedbackFormValues | undefined;
  showConfirmationDialog: boolean;
  isSuccess: boolean;
};

export const SeedListCancelPlanProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const search = useSearch({ from: "/cancelSeedListPlan" });

  const [state, setState] = useState<SeedListCancelPlanState>({
    step: 1,
    selectedAssignedMailboxes: [] as string[],
    feedback: undefined,
    showConfirmationDialog: false,
    isSuccess: false,
  });

  const handleStateChange = (newPartialState: Partial<SeedListCancelPlanState>) => {
    setState((prevState) => ({ ...prevState, ...newPartialState }));
  };

  // Get subscription data from API
  const { data: activePlans } = useGetActiveSubscriptionPlans();

  // Get plan details from the new API
  const { data: planDetailData, isLoading: isLoadingPlanDetail } = useGetPlanDetail(
    search.subscriptionId,
    search.planId
  );

  const subscription = useMemo(() => {
    const found = activePlans?.find((sub) => sub._id === search.subscriptionId);
    
    // If not found and using mock data, create mock subscription
   
    
    return found;
  }, [activePlans, search.subscriptionId]);

  // Use plan details from the API
  const planDetails = planDetailData;

  const {
    mutateAsync: sendCancellationRequest,
    isPending: isCancellationPending,
  } = useSendPlanCancellationRequest();

  const handleConfirm = async () => {
    try {
      if (!planDetails || !subscription) {
        toast.error("Plan details or subscription not found");
        return;
      }

      if (!state.feedback) {
        toast.error("Please provide feedback before cancelling");
        return;
      }

      if (
        planDetails.quantity !== search.quantity &&
        planDetails.assignedMailboxes.length !== 0 &&
        state.selectedAssignedMailboxes.length === 0
      ) {
        toast.error("You must select at least one mailbox to cancel");
        return;
      }

      // Skip API call for mock data
      const isMockData = subscription._id === "mock-subscription-id" || search.planId === "mock-seed-list-plan-id";
      
      if (!isMockData) {
        const payload: IPlanCancellationRequestPayload = {
          feedback: {
            cancellationReason: formatSeedListCancellationReason(state.feedback),
            improvementSuggestions: state.feedback?.improvementFeedback,
            serviceRating: state.feedback?.rating || 0,
            valuableFeatures: state.feedback?.valuableFeatures,
            returnLikelihood: state.feedback?.returnLikelihood,
          },
          subscriptionId: subscription._id,
          planId: planDetails.isCustomPlan ? undefined : planDetails.planId,
          addonPlanId: planDetails.isCustomPlan ? planDetails.planId : undefined,
          quantity: search.quantity,
          assignedMailboxIds: state.selectedAssignedMailboxes.length > 0 
            ? state.selectedAssignedMailboxes 
            : planDetails.assignedMailboxes,
        };
        console.log(payload);

        // Validate payload before sending
        const validatedPayload = cancellationPayloadSchema.parse(payload);

        // Use validated payload for the API call
        await sendCancellationRequest(validatedPayload);
      } else {
        console.log("Mock cancellation - skipping API call");
      }

      window.cioanalytics?.track("plan_cancelled", {
        plan: planDetails.isCustomPlan ? "CUSTOM" : "SEED_LIST",
        subscriptionId: subscription._id,
        quantity: search.quantity,
        assignedMailboxCount: state.selectedAssignedMailboxes.length,
      });

      handleStateChange({
        showConfirmationDialog: false,
        isSuccess: true,
      });
      toast.success("Seed list subscription cancellation confirmed");
    } catch (error) {
      if (error instanceof z.ZodError) {
        // Format Zod error messages
        const errorMessages = error.errors.map((err) => err.message).join("\n");
        toast.error(`Validation Error: ${errorMessages}`);
      } else {
        toast.error("Error cancelling subscription");
        console.error(error);
      }
    }
  };

  return (
    <SeedListCancelPlanContext.Provider
      value={{
        state,
        handleStateChange,
        subscription,
        planDetails,
        searchParams: search,
        handleConfirm,
        isCancellationPending,
      }}
    >
      {children}
    </SeedListCancelPlanContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSeedListCancelPlanContext = () => {
  const context = useContext(SeedListCancelPlanContext);
  if (!context) {
    throw new Error("useSeedListCancelPlan must be used within a SeedListCancelPlanProvider");
  }
  return context;
};

